// Imported (not restated) so the app-wide Filter type can't promise operators the builder can't execute.
// Type-only import, so no server code reaches the client bundle.
import type { FilterOperator } from '@/repositories/UserFilterBuilder';

export type { FilterOperator };

export interface Filter {
    field: string;
    operator: FilterOperator;
    value?: any; // Optional since is_null and is_not_null don't require a value
    timezone?: string; // IANA zone a date value was entered in; display only, the value stays UTC
}

export type ToolLog = {
    message: string;
    execution_time?: number;
    input?: any;
    output?: any;
    /** A sandbox user's tool mock answered the call (docs/users.md, "Tool mocks"). */
    mocked?: boolean;
    data?: {
        sessionId?: string;
        aiParams?: {
            userDataNew?: [
                {
                    key: string;
                    value: any;
                }
            ];
        };
        userDataNew?: any;
    }
};

export type DataType = 'text' | 'number' | 'date' | 'select' | 'ai prompt' | 'boolean' | 'checkbox' | 'systemUserSelect';

// client-side data field definition for a DataSchemaProperty
export interface FieldDefinition {
    key: string;
    label: string;
    description?: string;
    type: DataType;
    searchable?: boolean;
    displayable?: boolean;
    options?: { value: string; label: string; icon?: React.ReactNode }[];
    placeholder?: string;
    icon?: React.ReactNode;
    promptId?: string;
    promptModel?: string;
    promptName?: string;
    promptVersion?: string;
    promptText?: string;
    render?: (value: any, record: any) => React.ReactNode;
    renderType?: string;
    'x-reserved'?: boolean;
    /** True for fields defined in agent manifest dataSchema (not user-editable) */
    internal?: boolean;
    /** From `x-mutability`: 'system'/'locked' fields refuse UI edits (see isFieldEditableByHumans). */
    mutability?: FieldMutability;
    /** From `x-visibility`: minimum agent role that may read the field. */
    visibility?: FieldVisibility;
}


export interface SelectableValue {
    value: string;
    label: string;
}

export interface SchemaFieldInput {
    title: string;
    type: DataType | null;
    description: string;
    selectableValues?: SelectableValue[];
    aiModel?: string;
    aiPrompt?: string;
    renderType?: string;
}

// server-side data Data Schema (each field rendered as UI elements with FieldDefinition)
export interface DataSchemaProperty {
    type: "string" | "number" | "boolean";
    title: string;
    description: string;
    'x-field-type': DataType | null;
    enum?: string[];
    format?: string;
    'x-ai-model'?: string;
    'x-ai-prompt'?: string;
    'x-searchable'?: boolean;
    'x-displayable'?: boolean;
    'x-icon-name'?: string;
    'x-render-type'?: string;
    'x-reserved'?: boolean;
    'x-selectable-values'?: SelectableValue[];
    /**
     * Who may write this field (docs/impl/104): 'agent' (default) — model-driven tools may write
     * it; 'system' — only server code and operator-configured rules; 'locked' — only
     * migrations/seed. Enforced at the shared user-data write paths, which is what makes routing
     * rules over non-agent fields trustworthy.
     */
    'x-mutability'?: FieldMutability;
    /**
     * Minimum agent role that may read this field: 'all' (default), 'admin', or 'superadmin'.
     * The read-side twin of x-mutability — enforced at API boundaries via src/lib/fieldVisibility.ts.
     */
    'x-visibility'?: FieldVisibility;
}

export type FieldMutability = 'agent' | 'system' | 'locked';

export type FieldVisibility = 'all' | 'admin' | 'superadmin';

export interface DataSchema {
    $schema: string;
    type: 'object';
    title: string;
    properties: Record<string, DataSchemaProperty>;
    required?: string[];
}


// Auth User Management Types
// Note: This matches the Supabase User type from @supabase/supabase-js
// Sensitive token fields are not exposed through the standard User type

export interface UserMetadata {
    promptSelections?: Record<string, number>;
    agentVersionPreferences?: Record<number, number>;
    [key: string]: any;
}

export interface AuthUser {
    id: string;
    email: string;
    phone?: string;
    email_confirmed_at?: string;
    phone_confirmed_at?: string;
    last_sign_in_at?: string;
    created_at: string;
    updated_at?: string;
    user_metadata?: UserMetadata;
    app_metadata?: Record<string, any>;
    aud?: string;
    role?: string;
}

export interface AuthUserCreateRequest {
    email: string;
    password?: string;
    phone?: string;
    user_metadata?: UserMetadata;
    app_metadata?: Record<string, any>;
    email_confirm?: boolean;
    phone_confirm?: boolean;
}

export interface AuthUserUpdateRequest {
    email?: string;
    password?: string;
    phone?: string;
    user_metadata?: UserMetadata;
    app_metadata?: Record<string, any>;
    email_confirm?: boolean;
    phone_confirm?: boolean;
    ban_duration?: string;
}

export interface AuthUserListResponse {
    users: AuthUser[];
    total: number;
}

export interface AuthUserResponse {
    user: AuthUser;
}

export interface AuthUserDeleteResponse {
    success: boolean;
    user_id: string;
}

export interface AuthUserResetPasswordResponse {
    success: boolean;
    message: string;
}

export interface ViewActionDefinition {
    id: string;                      // UUID
    name?: string;                   // Required-in-UI name for the action definition
    description?: string;
    icon?: string;                   // Snapshot at attach time, so synthesized actions (e.g. run-subagent:<type>)
                                     // keep their custom icon in automations UI
    actionId: string;                // Reference to action manifest ID
    pollingInterval: number;         // Seconds
    lastRunAt: string | null;        // ISO timestamp
    enabled: boolean;                // Toggle for pausing without deletion
    params: Record<string, any>;
    createdAt: string;               // ISO timestamp
    createdBy?: string;              // Auth user ID who created this (used as actor for execution)

    maxExecutionsPerUser?: number | null;  // null = unlimited
    maxUsersPerRun?: number | null;        // null = unlimited
    maxTotalExecutions?: number | null;    // null = unlimited
    rateLimitSeconds?: number | null;     // null = 900s default, minimum 900s

    consecutiveFailures: number;     // Track failures for auto-disable
    lastFailureAt: string | null;
    disabledReason?: string;

    // Set when action is executing, cleared when done.
    currentRunId?: string;               // actionRunId of the currently executing run
    currentRunStartedAt?: string;        // ISO timestamp
    currentRunTotalUsers?: number;
    currentRunEligibleUsers?: number;
    // Tracked here (not in user_history) because skipped outcomes don't create user_history rows.
    currentRunSkippedCount?: number;
}

export interface ViewActionExecutionContext {
    viewId: number;
    viewSlug: string;
    definitionId: string;
    actionId: string;
    params: Record<string, any>;
}

/** Created by ViewActionPoller.recordUserHistory(); queried by the runs API route and ViewActionPoller rate limiting. */
export interface ViewActionHistoryData {
    /** UUID, primary query key */
    definitionId: string;
    viewSlug: string;
    /** e.g. 'log-event', 'dispute-billing' */
    actionId: string;
    /** UUID linking user executions to a run */
    actionRunId?: string;
    success: boolean;
    timestamp: string;
    result?: {
        success: boolean;
        skipped?: boolean;
        message: string;
        data?: unknown;
    };
    /** Kept for backward compatibility */
    output?: unknown;
    /** Kept for backward compatibility */
    error?: string;
}

/** Keyed by definitionId; avoids user_history queries for rate limiting and max-execution checks. */
export type UserViewActionMeta = Record<string, {
        executionCount: number;
        lastExecutedAt: string;
    }>;

export interface PromptVersionInfo {
    promptType: string;       // e.g. "metab-agent"
    trackingField: string;    // JSONB field name, e.g. "subagent_version_metab_agent"
    trackingValue: number | string;    // Version number, e.g. 5 (legacy prompt_version_* fields: "metab-agent-5")
}

export interface PromptResolutionMeta {
    promptVersions: PromptVersionInfo[];  // Main prompt + all sub-prompts used during interpolation
}

import type { users } from '@/server/db/schema';

type DrizzleUser = typeof users.$inferSelect;

type BaseUser = Omit<DrizzleUser, 'userType'> & {
    lastResponseAt: Date | null;
    // Declared explicitly because DrizzleUser infers from PgTableWithColumns<any>, which carries no
    // statically-known columns — without these, User fails TS's weak-type check at call sites.
    recipientId: string;
    email: string | null;
    externalId: string | null;
    archivedAt: Date | null;
    doNotContact: boolean;
    hasLinkedUsers?: boolean;  // Computed field: true if user has related users (same testInitialUserId)
};

export type LiveUser = BaseUser & {
    userType: 'live';
    testUser: false;
    testStatus: null;
    priority: number;
    status: 'active' | 'unresponsive' | 'success' | 'error' | 'dnc';
    data: Record<string, any>;

    // Not applicable to live users
    testClonedFromUserId: null;
    testInitialUserId: null;
    testDiscussionUserId: null;
    testDiscussionTitle: null;
    testDiscussionTitleEmbedding: null;
    testDiscussionStatus: null;
    testWhatWentWrong: null;
    testExpectedResponse: null;
    testSuccessCriteria: null;
    testEvalTitle: null;
    testEvalTitleEmbedding: null;
};

export type SandboxUser = BaseUser & {
    userType: 'sandbox';
    testUser: true;
    testStatus: 'sandbox';

    // Sandbox is for experimentation - allow any fields
    [key: string]: any;
};

export type DiscussionUser = BaseUser & {
    userType: 'discussion';
    testUser: true;
    testStatus: 'error_severe' | 'error_moderate' | 'error_minor' | 'neutral' | 'positive_example' | 'excellent_example';
    testDiscussionStatus: 'open' | 'closed';

    data: Record<string, any>;
    testClonedFromUserId: number;
    testInitialUserId: number | null;
    testDiscussionUserId: number;  // Self-reference
    testDiscussionTitle: string;
    testDiscussionTitleEmbedding: number[];
    testWhatWentWrong: string | null;  // @deprecated

    // Not applicable
    testExpectedResponse: null;
    testSuccessCriteria: null;
    testEvalTitle: null;
    testEvalTitleEmbedding: null;
    priority: null;
    status: null;
};

export type EvalUser = BaseUser & {
    userType: 'eval';
    testUser: true;
    testStatus: 'successful_example' | 'eval';

    data: Record<string, any>;
    testClonedFromUserId: number;
    testInitialUserId: number | null;
    testDiscussionUserId: number | null;
    testEvalTitle: string;
    testEvalTitleEmbedding: number[];
    testExpectedResponse: string;
    testSuccessCriteria: string;

    // Not applicable
    testDiscussionTitle: null;
    testDiscussionTitleEmbedding: null;
    testDiscussionStatus: null;
    testWhatWentWrong: null;
    priority: null;
    status: null;
};

export type RatedUser = BaseUser & {
    userType: 'rated';
    testUser: true;
    testStatus: null;
    reviewStatus: 'good' | 'bad';  // thumbs up = good, thumbs down = bad

    data: Record<string, any> & {
        rated_by?: string;      // Supabase auth user ID of team member who rated
        thumb_message_id?: number;    // ID of the rated message
        thumb_comment?: string;       // User's feedback comment
        thumb_rating?: 'positive' | 'negative';  // Explicit rating type
        thumb_rated_at?: string;      // ISO timestamp of when the rating was left
    };
    testClonedFromUserId: number;
    testInitialUserId: number | null;
    testDiscussionUserId: number | null;

    // Not applicable to rated users (can be promoted to discussion/eval later)
    testDiscussionTitle: null;
    testDiscussionTitleEmbedding: null;
    testDiscussionStatus: null;
    testWhatWentWrong: null;
    testExpectedResponse: null;
    testSuccessCriteria: null;
    testEvalTitle: null;
    testEvalTitleEmbedding: null;
    priority: null;
    status: null;
};

export type EvalRunUser = BaseUser & {
    userType: 'eval_run';
    testUser: true;
    testStatus: string | null;

    data: Record<string, any> & {
        evalScenarioId: number;
        evalAgentVersionId: number;
        evalVersionLabel: string;
        evalBenchmark?: string;
    };
    /** The scenario; null after the scenario is deleted, so key on data.evalScenarioId instead. */
    testClonedFromUserId: number | null;
    testInitialUserId: number | null;
    testDiscussionUserId: number | null;
    testEvalTitle: string | null;
    testEvalTitleEmbedding: null;
    testExpectedResponse: string | null;
    testSuccessCriteria: string | null;

    // Not applicable
    testDiscussionTitle: null;
    testDiscussionTitleEmbedding: null;
    testDiscussionStatus: null;
    testWhatWentWrong: null;
    priority: null;
    status: null;
};

export type User = LiveUser | SandboxUser | DiscussionUser | EvalUser | RatedUser | EvalRunUser;

/** Used by forms and API endpoints that update users without strict type checking. */
export type UserUpdateDto = Partial<Omit<DrizzleUser, 'lastResponseAt'>> & {
    lastResponseAt?: Date | null;
    [key: string]: any;
};

export function isLiveUser(user: User): user is LiveUser {
    return user.userType === 'live';
}

export function isSandboxUser(user: User): user is SandboxUser {
    return user.userType === 'sandbox';
}

export function isDiscussionUser(user: User): user is DiscussionUser {
    return user.userType === 'discussion';
}

export function isEvalUser(user: User): user is EvalUser {
    return user.userType === 'eval';
}

export function isRatedUser(user: User): user is RatedUser {
    return user.userType === 'rated';
}

export function isEvalRunUser(user: User): user is EvalRunUser {
    return user.userType === 'eval_run';
}

export function isTestUser(user: User): user is SandboxUser | DiscussionUser | EvalUser | RatedUser | EvalRunUser {
    return user.userType !== 'live';
}

// Helper for exhaustive checking
export function assertNever(x: never): never {
    throw new Error('Unhandled user type: ' + JSON.stringify(x));
}
